/* eslint-disable react/prop-types */

import React, { useContext, useEffect, useRef, useState } from "react";
import { PropTypes } from "prop-types";
import { graphql } from "gatsby";
import Img from "gatsby-image";
import { AppContext } from "~context/AppContext";
import { DocumentContext } from "~context/DocumentContext";
import Button from "~components/Button";
import Footer from "~components/Footer";
import Layout from "~components/Layout";
import ProductGrid from "~components/ProductGrid";
import Newsletter from "~components/Newsletter";
import ScreenHeight from "~components/ScreenHeight";
import SEO from "~components/SEO";
import Cross from "~components/svg/Cross";
import { pushProductEvent } from "~utils/analytics";
import {
  getInventoryIdByVariantTitle,
  getInventoryLevelsByIds,
  getProductByHandle,
  parseProducts
} from "~utils/shopify";
import { fancyError } from "~utils/helpers";
import { useTimeout } from "~utils/hooks";

const ProductPage = ({ data, location }) => {
  const {
    addToCart,
    locationModalSeen,
    setLocationModalSeen,
    toteDelivery,
    setToteDelivery
  } = useContext(AppContext);
  const { device } = useContext(DocumentContext);

  const locationSubmitRef = useRef();

  //

  const { allShopifyAdminProduct, markdownRemark } = data;
  const { frontmatter } = markdownRemark;
  const products = parseProducts(data);
  const product = getProductByHandle(frontmatter.shopifyHandle, products);

  const [addableProduct, setAddableProduct] = useState({});
  const [expandedAttribute, setExpandedAttribute] = useState(null);
  const [inventoryLoaded, setInventoryLoaded] = useState(false);
  const [locationModalActive, setLocationModalActive] = useState(false);
  const [locationModalMode, setLocationModalMode] = useState(`default`);
  const [newSuburb, setNewSuburb] = useState(null);
  const [outOfStockVariants, setOutOfStockVariants] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});

  //

  const { allDataYaml } = data;

  let open = true;

  if (allDataYaml?.edges?.[0]?.node) {
    ({ open } = allDataYaml.edges[0].node);
  }

  //

  const setPurchaseVariant = variant => {
    if (!product) {
      return;
    }

    const productClone = JSON.parse(JSON.stringify(product));

    productClone.variant = variant;

    setAddableProduct(productClone);
  };

  const locationSubmitProxy = e => {
    e.preventDefault();

    if (locationSubmitRef?.current) {
      locationSubmitRef.current.click();
    }

    return false;
  };

  const locationFormSubmit = e => {
    e.preventDefault();

    if (!newSuburb) {
      return false;
    }

    setLocationModalActive(false);

    // fire / forget
    fetch(`${process.env.GATSBY_NETLIFY_FUNCTIONS}/write-to-google-sheet/`, {
      body: JSON.stringify({ Locations: newSuburb }),
      method: `POST`
    })
      .then(response => {
        // eslint-disable-next-line no-console
        console.log(`[google]: `, response);
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(error);
      });

    return false;
  };

  //----------------------------------------------------------------------------
  //
  // onload hook

  useEffect(() => {
    const parsedProduct = getProductByHandle(
      frontmatter.shopifyHandle,
      products
    );

    const defaultVariant = parsedProduct.variants[0];
    const defaultSelectedOptions = {};

    defaultVariant.selectedOptions.forEach(option => {
      defaultSelectedOptions[option.name] = option.value;
    });
    parsedProduct.variant = defaultVariant;

    pushProductEvent(parsedProduct, `productView`);
    setSelectedOptions(defaultSelectedOptions);
    setAddableProduct(parsedProduct);
  }, []);

  //----------------------------------------------------------------------------
  //
  // location modal

  useEffect(() => {
    if (locationModalActive) {
      setLocationModalSeen(true);
    }
  }, [locationModalActive]);

  useTimeout(() => {
    if (!locationModalSeen) {
      setLocationModalActive(true);
    }
  }, 4000);

  //----------------------------------------------------------------------------
  //
  // product initialisation hook

  useEffect(() => {
    if (!product || product.checkoutId) {
      return;
    }

    let inventoryIds = ``;

    product.variants.forEach((variant, variantIndex) => {
      const inventoryId = getInventoryIdByVariantTitle(
        allShopifyAdminProduct.edges,
        product.handle,
        variant.title
      );

      if (!inventoryId) {
        return;
      }

      variant.inventoryId = inventoryId;

      if (variantIndex === 0) {
        inventoryIds = inventoryId;
      } else {
        inventoryIds = `${inventoryIds},${inventoryId}`;
      }
    });

    //

    if (window.location.href.includes(`localhost`)) {
      setInventoryLoaded(true);
      setOutOfStockVariants([]);
    } else {
      getInventoryLevelsByIds(inventoryIds)
        .then(response => {
          response
            .json()
            .then(inventory => {
              if (!inventory?.inventory_levels?.[0]) {
                return;
              }

              const unavailableVariantIds = [];

              inventory.inventory_levels.forEach(inventoryLevel => {
                if (typeof inventoryLevel.available === `undefined`) {
                  return;
                }

                if (inventoryLevel.available <= 0) {
                  product.variants.forEach(variant => {
                    if (
                      variant.inventoryId === inventoryLevel.inventory_item_id
                    ) {
                      unavailableVariantIds.push(variant.id);
                    }
                  });
                }
              });

              setOutOfStockVariants(unavailableVariantIds);
            })
            .catch(error => {
              fancyError(
                `Error caught reading product inventory JSON: ${error.message}`
              );
            });
        })
        .catch(error => {
          fancyError(
            `Error caught reading inventory from API: ${error.message}`
          );
        });
    }

    setPurchaseVariant(product.variant);
  }, [product]);

  //----------------------------------------------------------------------------
  //
  // option update hook

  useEffect(() => {
    if (!product) {
      return;
    }

    let matchedVariant;

    product.variants.forEach(variant => {
      if (matchedVariant) {
        return;
      }

      const maximumOptionScore = variant.selectedOptions.length;
      let matchedOptionScore = 0;

      variant.selectedOptions.forEach(variantSelectedOption => {
        if (matchedVariant) {
          return;
        }

        Object.keys(selectedOptions).forEach(userSelectedOptionKey => {
          if (
            variantSelectedOption.name === userSelectedOptionKey &&
            variantSelectedOption.value ===
              selectedOptions[userSelectedOptionKey]
          ) {
            matchedOptionScore += 1;
          }
        });
      });

      variant.matchedOptionScore = matchedOptionScore;

      if (matchedOptionScore === maximumOptionScore) {
        matchedVariant = variant;
      }
    });

    if (matchedVariant) {
      setPurchaseVariant(matchedVariant);
    }
  }, [selectedOptions]);

  useEffect(() => {
    if (outOfStockVariants) {
      setInventoryLoaded(true);
    }
  }, [outOfStockVariants]);

  //

  const stocked =
    inventoryLoaded && !outOfStockVariants.includes(product.variants[0].id);

  const isTote =
    product?.frontmatter?.shopifyHandle === `everlasting-daisy-hemp-tote`;

  //

  return (
    <>
      <SEO
        customTitle={frontmatter.title}
        customDescription={frontmatter.seoDescription}
        customKeywords={frontmatter.seoKeywords}
        path={location.pathname}
      />

      <ScreenHeight
        className={`location-modal transition-opacity ${
          locationModalActive
            ? `active opacity-100 pointer-events-auto`
            : `opacity-0 pointer-events-none`
        } w-screen fixed top-0 right-0 bottom-0 left-0 z-40 flex items-center justify-center pointer-events-none bg-dark-green`}
      >
        <button
          type="button"
          className="absolute top-0 right-0 z-20 mt-8 mr-6 p-2"
          onClick={() => setLocationModalActive(false)}
        >
          <Cross className="w-8" color="orange" />
        </button>

        {locationModalActive && (
          <div className="w-full relative flex items-center justify-center text-center">
            <div className="grid">
              {(locationModalMode === `default` && (
                <div className="grid-end-12 sm:grid-end-10 md:grid-end-8 sm:grid-start-2 md:grid-start-3 relative">
                  <h2 className="animation-appear-slow animation-delay-2 f2 text-cream">
                    We’re a delivery-only bakery.
                  </h2>
                  <h2 className="animation-appear-slow animation-delay-3 mt-2 f2 text-orange">
                    Our delivery pixies can only go as far as Preston, Reservoir
                    and Thornbury, Victoria.
                  </h2>

                  <Button
                    className="animation-appear-slow animation-delay-4 w-full md:w-3/4 relative block mt-8 mx-auto pt-6 pb-6 pointer-events-auto cursor-pointer"
                    color="orange"
                    onClick={() => setLocationModalActive(false)}
                  >
                    <span className="f-button">That’s okay</span>
                  </Button>

                  <div className="animation-appear-down-slow animation-delay-5 w-full md:w-3/4 relative mt-6 flex items-end justify-center md:justify-end">
                    <button
                      type="button"
                      className="pointer-events-auto cursor-pointer"
                      onClick={() => setLocationModalMode(`form`)}
                    >
                      <p className="hover-underline b1 text-orange">
                        Pooh pooh, I don’t live there!
                      </p>
                    </button>
                  </div>
                </div>
              )) || (
                <form
                  className="animation-appear-down-slow animation-delay-3 grid-end-12 sm:grid-end-10 md:grid-end-8 sm:grid-start-3 md:grid-start-3 relative"
                  onSubmit={locationFormSubmit}
                >
                  <input
                    ref={locationSubmitRef}
                    className="w-0 h-0 absolute opacity-0 pointer-events-none"
                    type="submit"
                  />
                  <h2 className="f2 text-cream">Oh no, we’re sorry!</h2>
                  <h2 className="mt-2 f2 text-orange">
                    Tell us where you wish we’d deliver in future.
                  </h2>

                  <input
                    className="w-full h-16 relative block mt-8 mb-4 border-b-orange f4 text-orange text-center"
                    placeholder="Enter your suburb"
                    onChange={e => setNewSuburb(e.currentTarget.value)}
                    required
                    type="text"
                  />

                  <Button
                    className="w-3/4 relative block mt-8 mx-auto pt-6 pb-6 pointer-events-auto cursor-pointer"
                    color="orange"
                    onClick={locationSubmitProxy}
                  >
                    <span className="f-button">Submit</span>
                  </Button>
                </form>
              )}
            </div>
          </div>
        )}
      </ScreenHeight>

      <Layout className="product-page w-full relative bg-dark-green text-cream">
        <section className="w-full relative md:flex mb-8 md:mb-24">
          {product && (
            <div className="product-page__gallery animation-appear-right animation-delay-2 relative">
              <div className="w-3/4 md:absolute md:top-0 md:left-0 z-10 pt-4 md:pt-10 pl-5 md:pl-16 pr-12 md:pr-0 mb-12 sm:mb-4 md:pb-0">
                <h3
                  className={`animation-appear-down-slow animation-delay-3 b1 text-${product
                    .frontmatter.noteUpperColor || `orange`}`}
                >
                  {product.frontmatter.noteUpper}
                </h3>
                <h4
                  className={`animation-appear-down-slow animation-delay-4 b1 text-${product
                    .frontmatter.noteLowerColor || `cream`}`}
                >
                  {product.frontmatter.noteLower}
                </h4>
              </div>

              {device &&
                device === `desktop` &&
                product?.frontmatter?.image?.childImageSharp?.fluid && (
                  <Img
                    className="w-full h-full relative object-cover"
                    fluid={product.frontmatter.image.childImageSharp.fluid}
                    alt={product.handle}
                  />
                )}

              {device &&
                device !== `desktop` &&
                product?.frontmatter?.image?.childImageSharp?.fluid && (
                  <div className="square overflow-hidden">
                    <figure className="w-full h-full absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center">
                      <Img
                        className="w-full h-full relative block object-cover"
                        fluid={product.frontmatter.image.childImageSharp.fluid}
                        alt={product.handle}
                      />
                    </figure>
                  </div>
                )}
            </div>
          )}

          {product && (
            <div className="product-page__sidebar animation-appear-left animation-delay-2 md:h-screen md:sticky md:top-0 md:left-0 flex flex-col items-center justify-center">
              <div className="h-full self-stretch overflow-y-scroll pt-12 md:pt-48">
                <article className="w-full sm:w-3/5 md:w-full relative px-6 sm:px-0 sm:mx-auto md:px-8 text-center">
                  <h1
                    className={`w-full ${
                      device && device === `desktop`
                        ? `f3`
                        : `f-button f-button--heading`
                    }`}
                  >
                    {frontmatter.title}
                  </h1>

                  {product?.frontmatter?.description && (
                    <p className="w-full mt-8 b1">
                      {product.frontmatter.description}
                    </p>
                  )}

                  <h3 className="w-full mt-3 f4">
                    {product.variants[0].price}
                  </h3>
                </article>

                {product?.frontmatter?.attributes?.[0] && (
                  <ul className="w-full relative block mt-8">
                    {product.frontmatter.attributes.map(attribute => {
                      const { key } = attribute;
                      const isExpanded = expandedAttribute === key;

                      return (
                        <li key={key}>
                          <button
                            type="button"
                            className="w-4/5 sm:w-1/2 md:w-4/5 relative block mx-auto pt-2 pb-4"
                            onClick={() =>
                              setExpandedAttribute(isExpanded ? null : key)
                            }
                          >
                            <span className="b1 text-orange">
                              {key} {isExpanded ? `-` : `+`}
                            </span>

                            <img
                              className="w-full absolute right-0 bottom-0 left-0"
                              src="/uploads/squiggle-sm.svg"
                              alt="Squiggle line"
                            />
                          </button>

                          {isExpanded && (
                            <p className="animation-appear-down-slow w-3/4 sm:w-3/5 md:w-3/4 mt-4 mx-auto mb-4 whitespace-pre-wrap b1 text-center text-light-green">
                              {attribute.value}
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {isTote && (
                  <article className="w-full sm:w-3/5 md:w-full relative mx-auto px-12 sm:px-0 md:px-12">
                    <h3 className="mt-12 mb-8 b1 text-center">
                      To reduce packaging waste, we don’t deliver bakery orders
                      in single-use bags. Would you like us to deliver your
                      bakery products in this bag?
                    </h3>

                    <label
                      id="tote-true-label"
                      htmlFor="tote-true"
                      className="w-full relative flex items-center justify-center mb-3"
                    >
                      <input
                        id="tote-true"
                        className="product-page__radio w-8 md:w-12 h-8 md:h-12 relative block cursor-pointer"
                        onChange={() => setToteDelivery(true)}
                        name="tote-delivery"
                        type="radio"
                        value="tote-true"
                        checked={toteDelivery}
                      />
                      <span className="w-32 ml-4 b1">Yes please</span>
                    </label>

                    <label
                      id="tote-false-label"
                      htmlFor="tote-false"
                      className="w-full relative flex items-center justify-center mb-3"
                    >
                      <input
                        id="tote-false"
                        className="product-page__radio w-8 md:w-12 h-8 md:h-12 relative block cursor-pointer"
                        onChange={() => setToteDelivery(false)}
                        name="tote-delivery"
                        type="radio"
                        value="tote-false"
                        checked={!toteDelivery}
                      />
                      <span className="w-32 ml-4 b1">No thanks</span>
                    </label>
                  </article>
                )}

                {product?.frontmatter?.withQuantity && (
                  <div className="w-full relative flex items-center justify-center mt-12 mb-12 md:mb-20">
                    <h2 className="mr-8 f4">Qty</h2>

                    <button
                      type="button"
                      className="product-page__quantifier block bg-light-green f4 text-center text-cream"
                      onClick={() => {
                        if (quantity < 2) {
                          return;
                        }

                        setQuantity(parseInt(quantity) - 1);
                      }}
                    >
                      <span className="block -mt-1">-</span>
                    </button>

                    <input
                      className="product-page__quantifier block bg-cream f4 text-center text-dark-green"
                      onChange={e => setQuantity(parseInt(e.target.value))}
                      type="number"
                      value={quantity}
                    />

                    <button
                      type="button"
                      className="product-page__quantifier block bg-light-green f4 text-center text-cream"
                      onClick={() => {
                        if (
                          product?.frontmatter?.purchaseLimit &&
                          quantity >=
                            parseInt(product?.frontmatter?.purchaseLimit)
                        ) {
                          return;
                        }

                        setQuantity(parseInt(quantity) + 1);
                      }}
                    >
                      <span className="block -mt-1">+</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="w-full sm:w-1/2 md:w-full relative block z-10">
                <Button
                  className={`transition-opacity ${
                    inventoryLoaded ? `` : `opacity-0 pointer-events-none`
                  } ${
                    open && stocked ? `` : `pointer-events-none`
                  } w-full relative block pt-5 pb-5`}
                  color={open && stocked ? `orange` : `cream`}
                  onClick={() => {
                    if (!open || !stocked) {
                      return;
                    }

                    addToCart(addableProduct, quantity);
                  }}
                >
                  <span className="f-button">
                    {open && stocked ? `Add to cart` : `Sold out`}
                  </span>
                </Button>
              </div>
            </div>
          )}
        </section>

        <ProductGrid
          excludedId={product?.id}
          heading="Related Products"
          open={open}
          products={products}
        />

        <Newsletter />
      </Layout>

      <Footer />
    </>
  );
};

ProductPage.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      frontmatter: PropTypes.shape({})
    })
  }).isRequired,
  location: PropTypes.shape({}).isRequired
};

export default ProductPage;

export const query = graphql`
  query ProductPage($handle: String!, $markdownId: String!) {
    allDataYaml {
      edges {
        node {
          open
        }
      }
    }

    markdownRemark(id: { eq: $markdownId }) {
      frontmatter {
        title

        image {
          childImageSharp {
            fluid(maxWidth: 1024, quality: 100) {
              ...GatsbyImageSharpFluid_withWebp_noBase64
            }
          }
          publicURL
        }

        shopifyHandle
        seoDescription
        seoKeywords
      }
    }

    allMarkdownRemark(
      filter: { frontmatter: { templateKey: { eq: "product-page" } } }
    ) {
      edges {
        node {
          fields {
            slug
          }

          frontmatter {
            title
            shopifyHandle
            priority
            purchaseLimit
            withQuantity
            noteUpper
            noteUpperColor
            noteLower
            noteLowerColor

            image {
              childImageSharp {
                fluid(maxWidth: 1920, quality: 100) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
              publicURL
            }
            description

            attributes {
              key
              value
            }
          }
        }
      }
    }

    shopifyProduct(handle: { eq: $handle }) {
      id
      handle
      title
      description
      images {
        originalSrc
      }
      productType
      vendor
      variants {
        id
        sku
        title
        image {
          originalSrc
        }
        price
        selectedOptions {
          name
          value
        }
      }
    }

    allShopifyProduct {
      edges {
        node {
          id
          title
          description
          handle
          images {
            originalSrc
          }
          variants {
            id
            sku
            title
            image {
              originalSrc
            }
            price
            selectedOptions {
              name
              value
            }
          }
        }
      }
    }

    allShopifyAdminProduct {
      edges {
        node {
          products {
            alternative_id
            handle
            variants {
              alternative_id
              inventory_item_id
              title
            }
          }
        }
      }
    }
  }
`;
